import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import { MODULES_KEY, PERMISSION_OBJECT, REGEX } from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import LoaderComponent from '../../components/LoaderComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import { EDIT_RELEASE } from './graphql/Mutations';
import { PROJECT_FOR_RELEASE, RELEASE } from './graphql/Queries';

function ViewRelease() {
  const { releaseId } = useParams();
  const history = useHistory();
  const [form] = Form?.useForm();
  const { dispatch } = useContext(AppContext);
  const [editableButton, setEditableButton] = useState(false);
  const [projectSearchString, setProjectSearchString] = useState('');
  const [selectedProject, setSelectedProject] = useState(undefined);

  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.RELEASES,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  const handleProjectSearch = debounce((value) => {
    setProjectSearchString(value?.toLowerCase());
  }, 500);

  const { loading } = useQuery(RELEASE, {
    variables: { releaseId },
    skip: !releaseId,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (res?.release) {
        const project = {
          label: res?.release?.projectName,
          value: res?.release?.projectId,
        };
        form?.setFieldsValue({
          ...res?.release,
          projectId: res?.release?.projectId
            ? {
              label: res?.release?.projectName,
              value: res?.release?.projectId,
            }
            : undefined,
          releaseDate: res?.release?.releaseDate
            ? dayjs(res?.release?.releaseDate)
            : null,
        });
        setSelectedProject(project);
      }
    },
  });

  const handleBack = () => {
    history?.goBack();
  };

  const [editRelease] = useMutation(EDIT_RELEASE, {
    onCompleted: () => {
      setEditableButton(false);
      history?.goBack();
    },
    onError: () => {
      message.error('Failed to update release');
    },
    refetchQueries: [{ query: RELEASE, variables: { releaseId } }],
  });

  const onFinish = (values) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    editRelease({
      variables: {
        id: releaseId,
        input: {
          releaseNumber: values?.releaseNumber,
          description: values?.description,
          releaseDate: values?.releaseDate
            ? values?.releaseDate?.toISOString()
            : null,
          projectId: values?.projectId?.value,
          projectName: values?.projectId?.label,
        },
      },
    });
  };

  const handleSave = () => {
    form?.submit();
  };

  const handleProjectSelect = (value) => {
    setSelectedProject(value);
    setProjectSearchString('');
  };

  const disabledDate = (current) =>
    current && current?.isBefore(dayjs()?.startOf('day'));

  return (
    <div className="table-card-page">
      <Card
        size="small"
        className="ant-body-scroll"
        title={
          <div className="users-sidebar">
            <div className="project-filter-left">
              <Button
                type="text"
                shape="square"
                onClick={handleBack}
                icon={<ArrowLeftOutlined />}
              />
              <Divider type="vertical" />
              View Release
            </div>
            <div className="project-filter-right">
              {isUpdatePermission && (
                <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={!editableButton || loading}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        }
      >
        {loading && <LoaderComponent />}
        <div className="card-body-wrapper">
          <div>
            <Form
              form={form}
              onFinish={onFinish}
              onFieldsChange={() => {
                if (form?.isFieldsTouched()) {
                  setEditableButton(true);
                  dispatch({
                    type: 'SET_SHOW_PROMPT',
                    data: true,
                  });
                }
              }}
              layout="vertical"
              className="p-24"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="releaseNumber"
                    label="Release Number:"
                    rules={[
                      {
                        required: true,
                        message: 'Please Input Release Number!',
                      },
                      {
                        pattern: REGEX.RELEASE_NUMBER,
                        message:
                          'Please enter a valid Release Number! (e.g., REL-1.0.0 or 1.0.0+123)',
                      },
                    ]}
                    extra="Format examples: REL-1.0.0, 1.0.0+123"
                  >
                    <Input
                      placeholder="Enter Release Number"
                      disabled={!isUpdatePermission}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="projectId"
                    label="Project:"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Project!',
                      },
                    ]}
                  >
                    <CommonSelect
                      placeholder="Select Project"
                      allowClear
                      labelInValue
                      query={PROJECT_FOR_RELEASE}
                      onSearch={handleProjectSearch}
                      onChange={handleProjectSelect}
                      responsePath="projectsForRelease.projects"
                      valuePath="id"
                      labelPath="name"
                      fetchPolicy="network-only"
                      value={selectedProject}
                      variables={{
                        filters: {
                          searchString: projectSearchString,
                          sortOrder: 'DESC',
                          sortField: 'createdOn',
                        },
                      }}
                      useEffectDeps={[projectSearchString]}
                      showSearch
                      virtual={false}
                      disabled={!isUpdatePermission}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="releaseDate"
                    label="Release Date:"
                    rules={[
                      {
                        required: true,
                        message: 'Please Select Date!',
                      },
                    ]}
                  >
                    <DatePicker
                      className="w-100"
                      disabled={!isUpdatePermission}
                      disabledDate={disabledDate}
                      placeholder="Select Date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="description" label="Description:">
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter Description"
                      disabled={!isUpdatePermission}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ViewRelease;
